import { useState, useEffect, forwardRef } from 'react';
import { MLPVersion, Theme } from '../../types/enums';
import { PaginationVersion, SortOrders } from '../types/enums';
import Pagination from '../Pagination/Pagination';
import { DynamicObjectInterface } from '../../../../sdk/type';
import { PaginationDataInterface, SortingDataInterface, TableProps } from '../types/propType';
import { handleSorting, TableBody, TableHeader } from '../TableComponents';
import { DropDown, FAIcon, IconStyle } from '../../';
import { DropDownData } from '../../Form/DropDown/types/propsType';
import styles from './AdminTable.module.scss';

const AdminTable = forwardRef<HTMLTableElement, TableProps>(
  ({
    columns,
    dataSource,
    rowKey,
    onRowClick,
    selected,
    setSelectedKeys,
    className,
    pagination = false,
    headerTheme = Theme.GRAY,
    sortingCallback,
    paginationObject,
    paginationCallback,
    enableCheckbox = false,
    formatPaginationCallback,
    forwardedRef,
    disabledItems,
    source,
    displayOverflow,
    showBulkAction = false,
    bulkActionOption,
    selectedAction,
    resetTrigger,
    clearAll,
    handleActionInputChange,
    checkedItemsCount = 0,
    setCheckedItemsCount,
    ...rest
  }: TableProps) => {
    const initialPagination = paginationObject || {
      totalRecords: dataSource.length,
      rowsPerPage: 10,
      currentPage: 0,
      recordStartIndex: 0,
      recordEndIndex: 10,
    };
    const [paginationData, setPaginationData] = useState<PaginationDataInterface>(initialPagination);
    const [sortingData, setSortingData] = useState<SortingDataInterface>({});
    const [tableData, setTableData] = useState<DynamicObjectInterface[]>(dataSource);

    useEffect(() => {
      setTableData(
        sortingCallback
          ? sortingCallback(columns[0].key, SortOrders.ASC, paginationData.currentPage)
          : handleSorting(sortingData, dataSource),
      );

      const storedPagination = sessionStorage.getItem('Pagination');
      setPaginationData(storedPagination && JSON.parse(storedPagination)?.page === window.location.pathname ? JSON.parse(storedPagination) : initialPagination);

    }, [dataSource]);

    const getWidth = (width = 0) => {
      if (width) {
        return { width: `${width}%` };
      }

      return {};
    };

    return (
      <>
        {(showBulkAction && bulkActionOption && checkedItemsCount > 0) && (
          <div className={`${styles['bulk-action-container']}`}>
            <label className={`${styles['bulk-action-selection-quantity']}`}>{`${checkedItemsCount} selected`}</label>
            <div className={`${styles['bulk-action-dropdown']}`}>
              <DropDown
                name={bulkActionOption.name}
                disabledFilter
                mlpVersion={MLPVersion.ONE}
                placeholder={bulkActionOption.placeholder}
                value={selectedAction?.value ? selectedAction.value : ''}
                data={bulkActionOption.dataset}
                configData={{
                  label: 'title',
                  icon: 'icon',
                  onSelect: (selectedOption: DropDownData) => {
                    // console.log('selectedOption', selectedOption);
                    if (typeof selectedOption.onClick === 'function') {
                      selectedOption.onClick();
                    }

                    if(selectedOption) handleActionInputChange?.(selectedOption);
                  },
                }}
                readOnly={false}
              />
            </div>
            {(selectedAction && selectedAction?.value) && (
              <div className={`${styles['bulk-action-clear-btn']}`}>
                <FAIcon
                  iconName='xmark'
                  onClick={() => {
                    clearAll?.();
                    setCheckedItemsCount?.(0);
                  }}
                  iconStyle={IconStyle.SOLID}
                  theme={Theme.ERROR}
                />
              </div>
            )}
          </div>
        )}

        <table
          className={`${styles['table-container']} ${className ? className : ''}`}
          {...rest}
          data-testid="table-data-test-id"
          ref={forwardedRef}
        >
          <TableHeader
            columns={columns}
            tableData={tableData}
            rowKey={rowKey}
            getWidth={getWidth}
            sortingData={sortingData}
            setSortingData={setSortingData}
            setTableData={setTableData}
            headerTheme={headerTheme}
            sortingCallback={sortingCallback}
            paginationData={paginationData}
            styles={styles}
            enableCheckbox={enableCheckbox}
            checkedItemsCount={checkedItemsCount}
            setCheckedItemsCount={setCheckedItemsCount}
            disabledItems={disabledItems}
          />
          <TableBody
            columns={columns}
            tableData={tableData}
            setTableData={setTableData}
            selected={selected}
            rowKey={rowKey}
            setSelectedKeys={setSelectedKeys}
            onRowClick={onRowClick}
            pagination={pagination}
            paginationData={paginationData}
            getWidth={getWidth}
            styles={styles}
            isServerSideFetch={!!paginationCallback}
            enableCheckbox={enableCheckbox}
            disabledItems={disabledItems}
            setCheckedItemsCount={setCheckedItemsCount}
            resetTrigger={resetTrigger}
            displayOverflow={displayOverflow}
          />
        </table>
        {pagination ? (
          <Pagination
            formatPaginationCallback={formatPaginationCallback}
            paginationVersion={PaginationVersion.ADMIN}
            {...paginationData}
            updatePaginationData={(updatedData) => {
              paginationCallback?.({
                ...paginationData,
                ...updatedData,
              });
              setPaginationData({
                ...paginationData,
                ...updatedData,
              });
              sessionStorage.setItem('Pagination', JSON.stringify({
                ...paginationData,
                ...updatedData,
                page: window.location.pathname,
              }));
            }}
            fromDashboard={source === 'dashboard'}
          />
        ) : (
          ''
        )}
      </>
    );
  },
);
AdminTable.displayName = 'AdminTable';

export default AdminTable;
export type { PaginationDataInterface };
